<script setup>
// The REAL app components, fed demo data, inside a feature's explainer. The
// same approach as the formats hub and the sports pages: the picture is always
// the current product, never a screenshot that drifts. Content is inert.
//
// `demo` keys are referenced from utils/features.js. Only features where the
// real thing says more than words get one.
import FixtureCell from '~/components/fixtures/FixtureCell.vue';
import LeagueCycleDivisionMatrix from '~/components/leagues/LeagueCycleDivisionMatrix.vue';
import LadderView from '~/components/ladders/LadderView.vue';
import LadderRules from '~/components/ladders/LadderRules.vue';
import BracketKnockoutStage from '~/components/tournaments/BracketKnockoutStage.vue';
import AmericanoStandingsTable from '~/components/americanos/AmericanoStandingsTable.vue';
import MeetOccurrenceCard from '~/components/meets/MeetOccurrenceCard.vue';
import MeetTierTable from '~/components/meets/MeetTierTable.vue';
import HandicapMatchBanner from '~/components/handicaps/HandicapMatchBanner.vue';
import HandicapChangesTable from '~/components/handicaps/HandicapChangesTable.vue';
import StatsView from '~/components/stats/StatsView.vue';
import WidgetShell from '~/components/widgets/WidgetShell.vue';
import WidgetLeagueStandings from '~/components/widgets/WidgetLeagueStandings.vue';
import AppHomePreview from '~/components/marketing/AppHomePreview.vue';
import LoginForm from '~/components/auth/LoginForm.vue';
import MessageBubble from '~/components/messaging/MessageBubble.vue';
import TypingIndicator from '~/components/messaging/TypingIndicator.vue';
import MeetMatchCard from '~/components/meets/MeetMatchCard.vue';
import MeetAttendeeList from '~/components/meets/MeetAttendeeList.vue';
import FixtureWhenAndWhere from '~/components/fixtures/FixtureWhenAndWhere.vue';
import BracketGroupStage from '~/components/tournaments/BracketGroupStage.vue';
import AmericanoRoundList from '~/components/americanos/AmericanoRoundList.vue';
import ClubLivePreview from '~/components/clubs/create/ClubLivePreview.vue';
import AwaitingPlacementBanner from '~/components/placement/AwaitingPlacementBanner.vue';
import HandicapChip from '~/components/handicaps/HandicapChip.vue';
import LiveScoreboard from '~/components/scoring/LiveScoreboard.vue';
import {handicapStartDemos} from '~/utils/handicapDemos';

defineProps({
  demo: {type: String, required: true},
});

const {
  leagueDivisionDemo, ladderDemo, heroFixtureDemo, playedFixtureDemo,
  bracketSemiDemo, bracketFinalDemo, americanoStandingsDemo, meetDemo, statsDemo,
} = useMarketingDemoData();

// A ladder with every rule set, so the rules card has something to say.
const ladderRulesDemo = {
  ...ladderDemo,
  playersPerTeam: 1,
  maxRankingDifference: 3,
  maxRankingDifferenceBelow: 0,
  topPlayersCount: 2,
  maxDifferenceTopPlayers: 1,
  numberUnrestrictedChallenges: 1,
  concurrentChallengeMode: 1,
  challengeExpiryDays: 7,
  challengeRejectionRule: 1,
  challengeRejectionMoveDownPlaces: 1,
  inactivityDemotionEnabled: true,
  inactivityDemotionDays: 21,
  inactivityDemotionPlaces: 2,
  allowMultipleTeamsPerPlayer: false,
  sendToLevels: false,
};

// Cancellation tiers as a club might set them for a paid club night.
const tiersDemo = [
  {label: 'More than 24 hours before', percent: 0, active: false},
  {label: 'Within 24 hours', percent: 50, active: true},
  {label: 'Within 2 hours, or no-show', percent: 100, active: false},
];

// Three automatic handicap moves after three results.
const handicapChangesDemo = [
  {id: 'hc1', name: 'Priya K.', oldLabel: 'Scratch', oldValue: 0, newLabel: '+2', newValue: 2, reason: 2, note: 'Lost 1–3 to a stronger player'},
  {id: 'hc2', name: 'Alex M.', oldLabel: '+3', oldValue: 3, newLabel: '+1', newValue: 1, reason: 2, note: 'Won 3–0, a decisive result'},
  {id: 'hc3', name: 'Sam T.', oldLabel: '−1', oldValue: -1, newLabel: '−1', newValue: -1, reason: 2, note: 'Won 3–2, too close to move'},
];
const handicapName = (change) => change.name;
const handicapStartDemo = handicapStartDemos.Squash?.start ?? null;

// ── Chat: three bubbles and someone typing ──
const {user} = useAuthService();
const minutesAgo = (m) => new Date(Date.now() - m * 60_000).toISOString();
const chatPerson = (id, firstName, lastName) => ({id, firstName, lastName, imageFileName: null});
const dana = chatPerson('demo-u-dana', 'Dana', 'Reed');
const sam = chatPerson('demo-u-sam', 'Sam', 'Taylor');
const chatDemo = computed(() => {
  const me = user.value?.id ?? 'demo-u-me';
  const base = {messageType: 0, conversationId: 'demo-conv', readByUserIds: [], readByCount: 0, isReadByCurrentUser: true};
  return [
    {...base, id: 'dm1', senderUserId: dana.id, sender: dana, content: 'Court 2 is free at 7 on Thursday if that works?', createdAt: minutesAgo(14)},
    {...base, id: 'dm2', senderUserId: me, sender: chatPerson(me, 'You', ''), content: 'Perfect, see you there.', createdAt: minutesAgo(9), readByUserIds: [dana.id, sam.id], readByCount: 2},
    {...base, id: 'dm3', senderUserId: sam.id, sender: sam, content: 'I\'ll come and watch, loser buys the drinks', createdAt: minutesAgo(2)},
  ];
});
const typingDemo = [{id: dana.id, firstName: 'Dana', lastName: 'Reed'}];

// ── A club night's matches and its register ──
const meetTeam = (id, name, initials) => ({id, name, initials, players: []});
const meetFixturesDemo = [
  {id: 'mf1', homeTeam: meetTeam('mt1', 'Priya K. + Jo P.', 'PJ'), awayTeam: meetTeam('mt2', 'Alex M. + Mo H.', 'AM'), homeTeamResult: 11, awayTeamResult: 7, homeTeamPoints: 3, awayTeamPoints: 0, playedAt: minutesAgo(40), games: []},
  {id: 'mf2', homeTeam: meetTeam('mt3', 'Dana R. + Lee W.', 'DL'), awayTeam: meetTeam('mt4', 'Sam T. + Chris D.', 'SC'), homeTeamResult: null, awayTeamResult: null, homeTeamPoints: null, awayTeamPoints: null, playedAt: null, games: []},
];
const attendeesDemo = [
  {id: 'at1', playerId: 'p1', playerName: 'Priya Khan', playerImageFileName: null, waitlistPosition: null},
  {id: 'at2', playerId: 'p2', playerName: 'Alex Morgan', playerImageFileName: null, waitlistPosition: null},
  {id: 'at3', playerId: 'p3', playerName: 'Dana Reed', playerImageFileName: null, waitlistPosition: null},
  {id: 'at4', playerId: 'p4', playerName: 'Sam Taylor', playerImageFileName: null, waitlistPosition: null},
  {id: 'at5', playerId: 'p5', playerName: 'Jo Park', playerImageFileName: null, waitlistPosition: 1},
  {id: 'at6', playerId: 'p6', playerName: 'Mo Hassan', playerImageFileName: null, waitlistPosition: 2},
];

// ── A match with a time and a court ──
const whenDemo = (() => {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  d.setHours(19, 30, 0, 0);
  return d.toISOString();
})();
const scheduledFixtureDemo = {...heroFixtureDemo, id: 'demo-fx-when', scheduledAt: whenDemo, playingAreaName: 'Court 2', ladderChallengeId: null};

// ── Group stage of a tournament ──
const groupTeam = (id, name, initials, position, totalPoints) => ({id, name, initials, players: [], position, totalPoints});
const groupsDemo = [
  {id: 'g-a', name: 'Group A', advanceCount: 2, teams: [
    groupTeam('ga1', 'Dana R.', 'DR', 1, 9), groupTeam('ga2', 'Sam T.', 'ST', 2, 6), groupTeam('ga3', 'Priya K.', 'PK', 3, 3), groupTeam('ga4', 'Lee W.', 'LW', 4, 0),
  ]},
  {id: 'g-b', name: 'Group B', advanceCount: 2, teams: [
    groupTeam('gb1', 'Alex M.', 'AM', 1, 7), groupTeam('gb2', 'Jo P.', 'JP', 2, 6), groupTeam('gb3', 'Mo H.', 'MH', 3, 4), groupTeam('gb4', 'Chris D.', 'CD', 4, 1),
  ]},
];

// ── An americano round: four players, two courts ──
const amPlayer = (id, firstName, lastName) => ({id, firstName, lastName, fullName: `${firstName} ${lastName}`, initials: `${firstName[0]}${lastName[0]}`, imageFileName: null});
const amTeam = (id, a, b) => ({id, name: `${a.firstName} ${a.lastName[0]}. + ${b.firstName} ${b.lastName[0]}.`, initials: `${a.firstName[0]}${b.firstName[0]}`, players: [a, b]});
const sofia = amPlayer('ap1', 'Sofia', 'Marino'), jack = amPlayer('ap2', 'Jack', 'Turner'), priyaP = amPlayer('ap3', 'Priya', 'Patel'), leo = amPlayer('ap4', 'Leo', 'Fernandez');
const americanoRoundsDemo = [
  {id: 'ar1', americanoRound: 1, clubId: 'demo-club', homeTeam: amTeam('amt1', sofia, jack), awayTeam: amTeam('amt2', priyaP, leo), homeTeamResult: 6, awayTeamResult: 3, homeTeamApprovedAt: minutesAgo(50), awayTeamApprovedAt: minutesAgo(50), playingAreaName: 'Court 1', supportsLiveScoring: false},
  {id: 'ar2', americanoRound: 2, clubId: 'demo-club', homeTeam: amTeam('amt3', sofia, priyaP), awayTeam: amTeam('amt4', jack, leo), homeTeamResult: 5, awayTeamResult: 4, homeTeamApprovedAt: minutesAgo(20), awayTeamApprovedAt: minutesAgo(20), playingAreaName: 'Court 1', supportsLiveScoring: false},
  {id: 'ar3', americanoRound: 3, clubId: 'demo-club', homeTeam: amTeam('amt5', sofia, leo), awayTeam: amTeam('amt6', jack, priyaP), homeTeamResult: null, awayTeamResult: null, homeTeamApprovedAt: null, awayTeamApprovedAt: null, playingAreaName: 'Court 1', supportsLiveScoring: false},
];

// ── The club page, as the create wizard previews it ──
const clubPreviewDemo = {name: 'Riverside Racquets Club', city: 'Bristol', hasFixedAddress: true, location: null, logoUpload: null};

// ── Someone waiting to be placed ──
const placementDemo = {id: 'demo-placement', placementDescription: 'a division', canPickPartners: false, preferredPartners: [], preferredBy: [], entryFeeMinor: null, entryFeeCurrency: null};

// ── Handicap chips: a few values on one scheme ──
const handicapSettingsDemo = {
  id: 'demo-handicaps',
  scheme: {
    key: 'squash-points', notation: 0, direction: 0, unitPlural: 'points', appliesTo: 'game',
    options: [{value: -3, label: '−3'}, {value: -2, label: '−2'}, {value: -1, label: '−1'}, {value: 0, label: 'Scratch'}, {value: 1, label: '+1'}, {value: 2, label: '+2'}, {value: 3, label: '+3'}, {value: 4, label: '+4'}],
  },
};
const handicapChipsDemo = [
  {name: 'Dana Reed', value: -2}, {name: 'Alex Morgan', value: 0}, {name: 'Priya Khan', value: 2}, {name: 'Lee Walker', value: 4},
];

// ── Live scoring: a squash match in its third game ──
const liveStateDemo = {
  completed: false,
  currentTurnSide: 'home',
  currentServerNumber: null,
  path: [
    {unit: 'match', label: 'Match', home: 1, away: 1, homeDisplay: '1', awayDisplay: '1'},
    {unit: 'game', label: 'Game 3', home: 7, away: 5, homeDisplay: '7', awayDisplay: '5', statusDisplay: 'Game 3'},
  ],
  completedUnits: {game: [{unit: 'game', home: 11, away: 8}, {unit: 'game', home: 9, away: 11}]},
};

// ── Notification preferences: a few rows as the profile page lays them out ──
const notificationRowsDemo = [
  {title: 'Challenge received', email: true, push: true},
  {title: 'Result entered against you', email: true, push: true},
  {title: 'Match reminder', email: false, push: true},
  {title: 'Club announcement', email: true, push: false},
  {title: 'Session RSVP open', email: false, push: true},
];

// The league widget, built from the same division the matrix shows.
const widgetDivisionsDemo = [{
  name: 'Division 1',
  cycleName: 'Autumn 2026',
  playersPerTeam: 1,
  rows: leagueDivisionDemo.value.teams.map(team => ({
    position: team.position,
    teamName: team.name,
    played: team.fixtures.length,
    bonusPoints: team.bonusPoints,
    totalPoints: team.totalPoints,
  })),
}];
</script>

<template>
  <div class="fd" aria-hidden="true">
    <div v-if="demo === 'fixture'" class="fd-stack">
      <fixture-cell :fixture="playedFixtureDemo" :navigable="false" :allow-editing="false" :bordered="true"/>
      <fixture-cell :fixture="heroFixtureDemo" :navigable="false" :allow-editing="false" :bordered="true"/>
    </div>

    <league-cycle-division-matrix v-else-if="demo === 'leagueMatrix'" v-model="leagueDivisionDemo"/>

    <ladder-view v-else-if="demo === 'ladder'" :ladder="ladderDemo"/>

    <ladder-rules v-else-if="demo === 'ladderRules'" :ladder="ladderRulesDemo" :unrestricted-challenges="1"/>

    <div v-else-if="demo === 'bracket'" class="fd-bracket">
      <bracket-knockout-stage :stage="bracketSemiDemo" :round="1" :total-rounds="2"/>
      <bracket-knockout-stage :stage="bracketFinalDemo" :round="2" :total-rounds="2"/>
    </div>

    <americano-standings-table v-else-if="demo === 'americano'" :players="americanoStandingsDemo.players" has-set-scores/>

    <div v-else-if="demo === 'meet'" class="fd-stack">
      <meet-occurrence-card
          v-for="occurrence in meetDemo.occurrences"
          :key="occurrence.id"
          :meet="meetDemo.meet"
          :occurrence="occurrence"
          :my-attendance="meetDemo.myAttendance"/>
    </div>

    <div v-else-if="demo === 'tiers'" class="fd-card">
      <div class="fd-card-head">If you cancel</div>
      <meet-tier-table :rows="tiersDemo"/>
    </div>

    <handicap-match-banner v-else-if="demo === 'handicap' && handicapStartDemo" :start="handicapStartDemo"/>

    <handicap-changes-table
        v-else-if="demo === 'handicapHistory'"
        noun="Player"
        :changes="handicapChangesDemo"
        :name-for="handicapName"/>

    <stats-view v-else-if="demo === 'stats'" :stats="statsDemo" form-mode="recent"/>

    <widget-shell v-else-if="demo === 'widget'" title="Club Championship" club-name="Riverside Squash Club">
      <widget-league-standings :divisions="widgetDivisionsDemo"/>
    </widget-shell>

    <div v-else-if="demo === 'homeScreen'" class="fd-phone-wrap">
      <div class="fd-phone">
        <div class="fd-phone-screen">
          <div class="fd-phone-notch"/>
          <div class="fd-phone-viewport"><app-home-preview/></div>
        </div>
      </div>
    </div>

    <div v-else-if="demo === 'signIn'" class="fd-card fd-signin">
      <client-only>
        <login-form hide-hero/>
      </client-only>
    </div>

    <div v-else-if="demo === 'chat'" class="fd-card fd-chat">
      <message-bubble v-for="message in chatDemo" :key="message.id" :message="message"/>
      <typing-indicator :users="typingDemo"/>
    </div>

    <div v-else-if="demo === 'meetMatches'" class="fd-stack">
      <meet-match-card v-for="(fixture, i) in meetFixturesDemo" :key="fixture.id" :fixture="fixture" :title="`Match ${i + 1}`"/>
    </div>

    <div v-else-if="demo === 'attendees'" class="fd-card">
      <div class="fd-card-head">Going · 4 of 4 · waitlist 2</div>
      <meet-attendee-list :rows="attendeesDemo" show-positions/>
    </div>

    <fixture-when-and-where v-else-if="demo === 'whenAndWhere'" :fixture="scheduledFixtureDemo"/>

    <bracket-group-stage v-else-if="demo === 'groups'" :stages="groupsDemo" :advance-count="2"/>

    <americano-round-list v-else-if="demo === 'americanoRounds'" :fixtures="americanoRoundsDemo"/>

    <club-live-preview v-else-if="demo === 'clubPreview'" :item="clubPreviewDemo" first-name="Grant"/>

    <awaiting-placement-banner v-else-if="demo === 'placement'" :placement="placementDemo" entity-name="Club Championship"/>

    <div v-else-if="demo === 'handicapChips'" class="fd-card">
      <div v-for="row in handicapChipsDemo" :key="row.name" class="fd-chip-row">
        <span>{{ row.name }}</span>
        <handicap-chip :settings="handicapSettingsDemo" :value="row.value" size="default"/>
      </div>
    </div>

    <live-scoreboard v-else-if="demo === 'liveScore'" :state="liveStateDemo" :schema="{}" home-name="Chris Dale" away-name="Lee Walker"/>

    <div v-else-if="demo === 'notifications'" class="fd-card">
      <div class="fd-notif-head"><span>Notification</span><span>Email</span><span>Push</span></div>
      <div v-for="row in notificationRowsDemo" :key="row.title" class="fd-notif-row">
        <span>{{ row.title }}</span>
        <v-switch :model-value="row.email" color="primary" hide-details density="compact" inset/>
        <v-switch :model-value="row.push" color="primary" hide-details density="compact" inset/>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fd {
  pointer-events: none;
  user-select: none;
}

.fd-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fd-bracket {
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: hidden;
}

.fd-card {
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  padding: 14px 16px;
}

.fd-card-head {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
  margin-bottom: 8px;
}

/* The phone, as AppCallout draws it: 390 logical px scaled into a 268px screen */
.fd-phone-wrap {
  display: flex;
  justify-content: center;
}

.fd-phone {
  width: 290px;
  padding: 11px;
  border-radius: 44px;
  background: #1A1A1A;
  box-shadow: 0 34px 64px -24px rgba(26, 26, 26, 0.55), 0 12px 24px -16px rgba(26, 26, 26, 0.35);
}

.fd-phone-screen {
  position: relative;
  width: 268px;
  height: 580px;
  border-radius: 34px;
  overflow: hidden;
  background: rgb(var(--v-theme-light));
}

.fd-phone-notch {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 96px;
  height: 22px;
  border-radius: 0 0 14px 14px;
  background: #1A1A1A;
  z-index: 3;
}

.fd-phone-viewport {
  width: 390px;
  height: 844px;
  transform: scale(0.6872);
  transform-origin: top left;
}

.fd-signin {
  max-width: 420px;
  margin: 0 auto;
}

.fd-chat {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.fd-chip-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:last-child {
    border-bottom: 0;
  }
}

.fd-notif-head,
.fd-notif-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 64px 64px;
  align-items: center;
  gap: 8px;
}

.fd-notif-head {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
  padding-bottom: 6px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.fd-notif-row {
  font-size: 14px;
  padding: 2px 0;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:last-child {
    border-bottom: 0;
  }
}
</style>
